import React, { useState, useEffect } from 'react';
import {
    Box,
    HStack,
    Text,
    Button,
    Card,
    CardBody,
    CardHeader,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Input,
    Select,
    FormControl,
    FormLabel,
    Checkbox,
    VStack,
    Grid,
    GridItem,
    Flex,
    Tag,
    Icon,
} from '@chakra-ui/react';
import { LuActivity, LuBell, LuMail, LuUser, LuPencil } from 'react-icons/lu';


const OtherChannelsTab = ({ channels = {}, channelOptions = [], currencyList = [], onSave }) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDecisionEngineModalOpen, setIsDecisionEngineModalOpen] = useState(false);

    const [editChannelPricing, setEditChannelPricing] = useState({
        email: { rate: '', currency: '', marketing: '' },
        push_notification: { rate: '', currency: '', marketing: '' },
        in_app: { rate: '', currency: '', marketing: '' },
    });

    const [editDecisionEngine, setEditDecisionEngine] = useState({
        rate: '',
        currency: '',
        otpTransactionChannels: [],
        marketingChannels: [],
    });

    // Map channel IDs to icons
    const channelIcons = {
        email: LuMail,
        push_notification: LuBell,
        in_app: LuUser,
    };

    // Get regular channels (excluding api_charges)
    const regularChannels = Object.entries(channels)
    .filter(([id]) => id !== 'api_charges' && id !== 'mau');

    // Get api_charges (decision engine)
    const decisionEngine = channels.api_charges || {};

    useEffect(() => {
        // Initialize edit state when modal opens
        if (isEditModalOpen) {
            const newEditState = {};
            regularChannels.forEach(([id, data]) => {
                newEditState[id] = {
                    rate: data.price?.toString() || '',
                    marketing: data.marketing?.toString() || '',
                    currency: data.currency_id?.toString() || '',
                };
            });
            setEditChannelPricing(newEditState);
        }
    }, [isEditModalOpen, channels]);

    useEffect(() => {
        // Initialize decision engine edit state
        if (isDecisionEngineModalOpen && decisionEngine) {
            setEditDecisionEngine({
                rate: decisionEngine.price?.toString() || '',
                currency: decisionEngine.currency_id?.toString() || '',
                otpTransactionChannels: decisionEngine.channel_in_use
                    ?.filter(ch => ch.allow)
                    .map(ch => ch.channel_id) || [],
                marketingChannels: decisionEngine.channel_in_use_marketing
                    ?.filter(ch => ch.allow)
                    .map(ch => ch.channel_id) || [],
            });
        }
    }, [isDecisionEngineModalOpen, decisionEngine]);

    const formatCurrency = (amount, currencyId) => {
        if (!amount || !currencyId) return '-';

        const currencyObj = currencyList.find(c => c.id === parseInt(currencyId));
        const currencyCode = currencyObj?.code || 'USD';

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 4,
        }).format(amount);
    };

    const handleOpenEditModal = () => {
        setIsEditModalOpen(true);
    };

    const handleSaveChannelPricing = () => {
        // Prepare updated channels object
        const updatedChannels = { ...channels };

        Object.entries(editChannelPricing).forEach(([channelId, data]) => {
            if (updatedChannels[channelId]) {
                updatedChannels[channelId] = {
                    ...updatedChannels[channelId],
                    price: parseFloat(data.rate) || null,
                    marketing: parseFloat(data.marketing) || null,
                    currency_id: data.currency,
                    update_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
                };
            }
        });

        // Call parent save function
        if (onSave) {
            onSave(updatedChannels);
            setIsEditModalOpen(false);
        }

        
    };

    const handleOpenDecisionEngineModal = () => {
        setIsDecisionEngineModalOpen(true);
    };

    const handleSaveDecisionEngine = () => {
        const updatedChannels = {
            ...channels,
            api_charges: {
                ...channels.api_charges,
                price: parseFloat(editDecisionEngine.rate) || null,
                currency_id: editDecisionEngine.currency,
                channel_in_use: channelOptions.map(channel => ({
                    channel_id: channel.id,
                    allow: editDecisionEngine.otpTransactionChannels.includes(channel.id)
                })),
                channel_in_use_marketing: channelOptions.map(channel => ({
                    channel_id: channel.id,
                    allow: editDecisionEngine.marketingChannels.includes(channel.id)
                })),
                update_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
            }
        };

        if (onSave) {
            onSave(updatedChannels);
        }

        setIsDecisionEngineModalOpen(false);
    };

    const handleChannelToggle = (channelId, type) => {
        if (type === 'otp') {
            setEditDecisionEngine(prev => ({
                ...prev,
                otpTransactionChannels: prev.otpTransactionChannels.includes(channelId)
                    ? prev.otpTransactionChannels.filter(c => c !== channelId)
                    : [...prev.otpTransactionChannels, channelId]
            }));
        } else {
            setEditDecisionEngine(prev => ({
                ...prev,
                marketingChannels: prev.marketingChannels.includes(channelId)
                    ? prev.marketingChannels.filter(c => c !== channelId)
                    : [...prev.marketingChannels, channelId]
            }));
        }
    };

    return (
        <Box py={4}>
            

            {/* Email, Push, In-App Cards */}
            <Grid templateColumns="repeat(3, 1fr)" gap={6} mb={6}>
                {regularChannels.map(([channelId, channelData]) => {
                    const IconComponent = channelIcons[channelId] || LuActivity;

                    return (
                        <GridItem key={channelId}>
                            <Box className='box-style-card'>
                                <Box pb={2}>
                                    <HStack spacing={2}>

                                        <IconComponent size={16} color="#0a0a0a" />

                                        <Text fontSize="sm" color="#0a0a0a" fontWeight={"medium"}>{channelData.name}</Text>
                                    </HStack>
                                </Box>
                                <Box>
                                    <VStack align="stretch" spacing={4}>
                                        <Box>
                                            <Text fontSize="sm" color="black" fontWeight={"medium"} mb={1}>Marketing Message Rate</Text>
                                            <Text fontSize="2xl" fontWeight="semibold" color="#155dfc">
                                                {channelData.marketing
                                                    ? formatCurrency(channelData.marketing, channelData.currency_id)
                                                    : '-'}
                                            </Text>
                                        </Box>
                                        <Flex justify="space-between" align="center">
                                            <Box flex={1}>
                                                <Text fontSize="sm" color="black" fontWeight={"medium"} mb={1}>Transaction & OTP Rate</Text>
                                                <Text fontSize="2xl" fontWeight="semibold">
                                                    {channelData.price
                                                        ? formatCurrency(channelData.price, channelData.currency_id)
                                                        : '-'}
                                                </Text>
                                            </Box>
                                            <Text
                                                variant="link"
                                                color="#01acfb"
                                                size="sm"
                                                cursor={"pointer"}
                                                onClick={handleOpenEditModal}
                                            >
                                                View
                                            </Text>
                                        </Flex>
                                    </VStack>
                                </Box>
                            </Box>
                        </GridItem>
                    );
                })}
            </Grid>

            {/* Decision Engine Card */}
            {decisionEngine && decisionEngine.name && (
                <Box className='box-style-card'>
                    <Box>
                        <Flex justify="space-between" align="center">
                            <HStack spacing={2}>



                                <Text fontSize="sm" color="black" fontWeight={"medium"}>Decision Engine</Text>
                            </HStack>
                            <Button
                                leftIcon={<Icon as={LuPencil} />}
                                size="sm"
                                border={"1px solid #0000001a"}
                                color={"#0a0a0a"}
                                bg={"white"}
                                _hover={{
                                    bg: "#e2e8f0 !important",
                                    color: "#0a0a0a",
                                }}
                                onClick={handleOpenDecisionEngineModal}
                            >
                                Configure
                            </Button>
                        </Flex>
                    </Box>
                    <Box>
                        <VStack align="stretch" spacing={4}>
                            <Box>
                                <Text fontSize="xs" color="black" fontWeight={"medium"} mb={1}>Rate per Decision</Text>
                                <Text fontSize="lg" fontWeight="medium">
                                    {decisionEngine.price
                                        ? formatCurrency(decisionEngine.price, decisionEngine.currency_id)
                                        : '-'}
                                </Text>
                            </Box>
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                <Box>
                                    <Text fontSize="xs" color="black" fontWeight={"medium"} mb={2}>OTP & Transaction Channels</Text>
                                    <Flex flexWrap="wrap" gap={1}>
                                        {decisionEngine.channel_in_use
                                            ?.filter(ch => ch.allow)
                                            .map(ch => {
                                                const channel = channelOptions.find(opt => opt.id === ch.channel_id);
                                                return channel ? (
                                                    <Tag key={ch.channel_id} bg={"white"} border={"1px solid #0000001a"} color={"#0a0a0a"} fontSize="xs">
                                                        {channel.name}
                                                    </Tag>
                                                ) : null;
                                            })}
                                        {(!decisionEngine.channel_in_use ||
                                            decisionEngine.channel_in_use.filter(ch => ch.allow).length === 0) && (
                                                <Text fontSize="xs" color="gray.500">No channels selected</Text>
                                            )}
                                    </Flex>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="black" fontWeight={"medium"} mb={2}>Marketing & Informational Channels</Text>
                                    <Flex flexWrap="wrap" gap={1}>
                                        {decisionEngine.channel_in_use_marketing
                                            ?.filter(ch => ch.allow)
                                            .map(ch => {
                                                const channel = channelOptions.find(opt => opt.id === ch.channel_id);
                                                return channel ? (
                                                    <Tag key={ch.channel_id} bg={"white"} border={"1px solid #0000001a"} color={"#0a0a0a"} fontSize="xs">
                                                        {channel.name}
                                                    </Tag>
                                                ) : null;
                                            })}
                                        {(!decisionEngine.channel_in_use_marketing ||
                                            decisionEngine.channel_in_use_marketing.filter(ch => ch.allow).length === 0) && (
                                                <Text fontSize="xs" color="gray.500">No channels selected</Text>
                                            )}
                                    </Flex>
                                </Box>
                            </Grid>
                        </VStack>
                    </Box>
                </Box>
            )}

            {/* Edit Other Channels Modal */}
            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Configure Other Channels</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={6} align="stretch">
                            {regularChannels.map(([channelId, channelData]) => (
                                <Box key={channelId}>
                                    <Text fontSize="sm" color="black" fontWeight={"medium"} mb={3}>{channelData.name} Pricing</Text>
                                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight={"semibold"}>Transaction & OTP Rate</FormLabel>
                                            <Input
                                                type="number"
                                                step="0.0001"
                                                value={editChannelPricing[channelId]?.rate || ''}
                                                onChange={(e) => setEditChannelPricing(prev => ({
                                                    ...prev,
                                                    [channelId]: { ...prev[channelId], rate: e.target.value }
                                                }))}
                                                placeholder="e.g., 0.001"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs">Marketing Rate</FormLabel>
                                            <Input
                                                type="number"
                                                step="0.0001"
                                                value={editChannelPricing[channelId]?.marketing || ''}
                                                onChange={(e) => setEditChannelPricing(prev => ({
                                                    ...prev,
                                                    [channelId]: { ...prev[channelId], marketing: e.target.value }
                                                }))}
                                                placeholder="e.g., 0.00085"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs">Currency</FormLabel>
                                            <Select
                                                value={editChannelPricing[channelId]?.currency || ''}
                                                onChange={(e) => setEditChannelPricing(prev => ({
                                                    ...prev,
                                                    [channelId]: { ...prev[channelId], currency: e.target.value }
                                                }))}
                                                placeholder="Select currency"
                                            >
                                                {currencyList.map((currency) => (
                                                    <option key={currency.id} value={currency.id}>
                                                        {currency.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Box>
                            ))}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm"
                                border={"1px solid #0000001a"}
                                color={"#0a0a0a"}
                                bg={"white"}
                                _hover={{
                                    bg: "#e2e8f0 !important",
                                    color: "#0a0a0a",
                                }} mr={3} onClick={() => setIsEditModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button bg="#2563eb" color="white" hover={"#2563eb"} size={"sm"} onClick={handleSaveChannelPricing}>
                            Save Configuration
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Edit Decision Engine Modal */}
            <Modal isOpen={isDecisionEngineModalOpen} onClose={() => setIsDecisionEngineModalOpen(false)} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Configure Decision Engine</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel>Rate per Decision</FormLabel>
                                <Input
                                    type="number"
                                    step="0.0001"
                                    value={editDecisionEngine.rate}
                                    onChange={(e) => setEditDecisionEngine(prev => ({ ...prev, rate: e.target.value }))}
                                    placeholder="e.g., 0.001"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Currency</FormLabel>
                                {/* <Select
                  
                >
                  <option value="USD">🇺🇸 USD - US Dollar</option>
                  <option value="PKR">🇵🇰 PKR - Pakistani Rupee</option>
                </Select> */}
                                <Select
                                    value={editDecisionEngine.currency}
                                    onChange={(e) => setEditDecisionEngine(prev => ({ ...prev, currency: e.target.value }))}
                                    placeholder="Select currency"
                                >
                                    {currencyList.map((currency) => (
                                        <option key={currency.id} value={currency.id}>
                                            {currency.name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box>
                                <Text fontWeight="bold" mb={3}>OTP and transaction</Text>
                                <Box>
                                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                                        {channelOptions.map((channel) => (
                                            <Checkbox
                                                key={channel.id}
                                                isChecked={editDecisionEngine.otpTransactionChannels.includes(channel.id)}
                                                onChange={() => handleChannelToggle(channel.id, 'otp')}
                                            >
                                                {channel.label || channel.name}
                                            </Checkbox>
                                        ))}
                                    </Grid>
                                </Box>
                            </Box>

                            <Box>
                                <Text fontWeight="bold" mb={3}>Marketing</Text>
                                <Box>
                                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                                        {channelOptions.map((channel) => (
                                            <Checkbox
                                                key={channel.id}
                                                isChecked={editDecisionEngine.marketingChannels.includes(channel.id)}
                                                onChange={() => handleChannelToggle(channel.id, 'marketing')}
                                            >
                                                {channel.label || channel.name}
                                            </Checkbox>
                                        ))}
                                    </Grid>
                                </Box>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm"
                                border={"1px solid #0000001a"}
                                color={"#0a0a0a"}
                                bg={"white"}
                                _hover={{
                                    bg: "#e2e8f0 !important",
                                    color: "#0a0a0a",
                                }}  mr={3} onClick={() => setIsDecisionEngineModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button bg="#2563eb" color="white" hover={"#2563eb"} size={"sm"} onClick={handleSaveDecisionEngine}>
                            Save Configuration
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default OtherChannelsTab;